import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useCreateAddress } from '@/api/addresses/mutations/useCreateAddress'
import { useSetDefaultAddress } from '@/api/addresses/mutations/useSetDefaultAddress'
import { useUpdateAddress } from '@/api/addresses/mutations/useUpdateAddress'
import type { Address } from '@/api/addresses/types/Address'
import { AppDialog } from '@/components/AppDialog'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

type AddressFormData = {
  street: string
  apartment: string
  city: string
  postalCode: string
  note: string
}

const defaultValues: AddressFormData = {
  street: '',
  apartment: '',
  city: '',
  postalCode: '',
  note: '',
}

// Pattern to check if street is only numbers (moshav pattern from marketing site)
const moshavStreetsPattern = /^\d+$/

interface EditAddressDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  editingAddress: Address | null
  onSuccess?: (createdAddress?: Address) => void
}

export default function EditAddressDialog({ isOpen, onOpenChange, editingAddress, onSuccess }: EditAddressDialogProps) {
  const { mutateAsync: createAddress, isPending: isCreatingAddress } = useCreateAddress()
  const { mutateAsync: updateAddress, isPending: isUpdatingAddress } = useUpdateAddress(editingAddress?.userId)
  const { mutateAsync: setDefaultAddress } = useSetDefaultAddress()
  const [isDefault, setIsDefault] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddressFormData>({
    defaultValues,
  })

  useEffect(() => {
    if (editingAddress) {
      reset({ ...editingAddress })
      setIsDefault(editingAddress.isDefault)
    } else {
      reset(defaultValues)
      setIsDefault(false)
    }
  }, [editingAddress, isOpen, reset])

  const onSubmit = async (data: AddressFormData) => {
    try {
      if (editingAddress) {
        const isAddressChanged = JSON.stringify(data) !== JSON.stringify(editingAddress)
        if (isAddressChanged) {
          await updateAddress({
            addressId: editingAddress.id,
            address: data,
          })
        }
        console.log({ isAddressChanged, editingAddress, isDefault })

        if (isDefault && !editingAddress.isDefault) {
          await setDefaultAddress(editingAddress.id)
        }

        onOpenChange(false)
        reset(defaultValues)
        setIsDefault(false)
        onSuccess?.()
      } else {
        const createdAddress = await createAddress(data)

        if (isDefault) {
          await setDefaultAddress(createdAddress.id)
        }

        onOpenChange(false)
        reset(defaultValues)
        setIsDefault(false)
        onSuccess?.(createdAddress)
      }
    } catch (error) {
      console.error('Failed to save address:', error)
    }
  }

  return (
    <AppDialog isOpen={isOpen} onOpenChange={onOpenChange} title={editingAddress ? 'ערוך כתובת' : 'הוסף כתובת חדשה'}>
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col items-end gap-8'>
        <div className='flex w-full flex-col gap-[21px]'>
          <Controller
            name='city'
            control={control}
            rules={{ required: 'עיר הוא שדה חובה' }}
            render={({ field }) => (
              <TextField id='address-city' label='עיר' error={!!errors.city} supportingText={errors.city?.message} {...field} />
            )}
          />
          <Controller
            name='street'
            control={control}
            rules={{
              required: 'רחוב הוא שדה חובה',
              maxLength: {
                value: 25,
                message: 'שם הרחוב ארוך מדי (מקסימום 25 תווים)',
              },
              validate: value => {
                if (moshavStreetsPattern.test(value)) {
                  return 'שם הרחוב לא יכול להכיל רק מספרים'
                }
                return true
              },
            }}
            render={({ field }) => (
              <TextField id='address-street' label='רחוב' {...field} error={!!errors.street} supportingText={errors.street?.message} />
            )}
          />
          <div className='flex w-full gap-4'>
            <Controller
              name='postalCode'
              control={control}
              rules={{
                required: 'מיקוד הוא שדה חובה',
                validate: value => {
                  if (value && value.length === 7) {
                    return true
                  }
                  return 'מיקוד חייב להכיל 7 תווים'
                },
              }}
              render={({ field }) => (
                <TextField
                  id='address-postalcode'
                  label='מיקוד'
                  {...field}
                  error={!!errors.postalCode}
                  supportingText={errors.postalCode?.message}
                  className='flex-1'
                />
              )}
            />
            <Controller
              name='apartment'
              control={control}
              render={({ field }) => <TextField id='address-apartment' label='מספר דירה' {...field} className='flex-1' />}
            />
          </div>
          <Controller
            name='note'
            control={control}
            render={({ field }) => <TextField id='address-note' label='הערות לכתובת (לשימושכם האישי בלבד)' {...field} />}
          />
          <div className='flex items-center gap-2'>
            <Checkbox id='set-default' checked={isDefault} onCheckedChange={checked => setIsDefault(checked === true)} />
            <label htmlFor='set-default' className='text-text-secondary cursor-pointer text-sm' dir='rtl'>
              קבע ככתובת ראשית למשלוח
            </label>
          </div>
        </div>
        <div className='flex w-full justify-center'>
          <Button
            type='submit'
            size='lg'
            loading={isCreatingAddress || isUpdatingAddress}
            disabled={isCreatingAddress || isUpdatingAddress}
            className='w-full max-w-[335px]'
          >
            {editingAddress ? 'שמירת שינויים' : 'שמירת כתובת חדשה'}
          </Button>
        </div>
      </form>
    </AppDialog>
  )
}
